import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../lib/theme';
import { supabase } from '../lib/supabase';
import i18n from '../lib/i18n';

function LanguageToggle({ light = true }) {
  const isEn = i18n.language.startsWith('en');
  return (
    <button
      onClick={() => i18n.changeLanguage(isEn ? 'es' : 'en')}
      style={{
        background: light ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
        border: light ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(0,0,0,0.15)',
        borderRadius: 8,
        color: light ? '#fff' : C.carbon,
        fontSize: '0.72rem',
        padding: '0.35rem 0.6rem',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {isEn ? '🇪🇸 ES' : '🇬🇧 EN'}
    </button>
  );
}

export { LanguageToggle };

// showCodeEntry === true significa que App.js ya nos ha enrutado aquí con una
// sesión (normalmente anónima) existente sin clínica aún — no hace falta
// crear una nueva. En el otro caso (visitante nuevo, sin sesión) creamos la
// sesión anónima nada más montar, una sola vez.
export default function LandingChat({ onSignUpAnonymous, onCodeRedeemed, showCodeEntry = false }) {
  const { t } = useTranslation();
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const anonSignupStarted = useRef(false);

  useEffect(() => {
    if (showCodeEntry || anonSignupStarted.current) return;
    anonSignupStarted.current = true;
    onSignUpAnonymous?.().catch(err => console.error('Error creando sesión anónima:', err));
  }, [showCodeEntry, onSignUpAnonymous]);

  async function handleRedeemCode(e) {
    e.preventDefault();
    setCodeError('');
    setCodeLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/clinic/codes/redeem', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({ code: codeInput }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'code_not_found') setCodeError(t('landing.code_not_found'));
        else if (data.error === 'code_already_used' || data.error === 'code_in_use') setCodeError(t('landing.code_used'));
        else if (data.error === 'code_expired') setCodeError(t('landing.code_expired'));
        else if (data.error === 'already_linked') setCodeError(t('landing.code_already_linked'));
        else setCodeError(t('landing.error_unknown'));
        return;
      }
      onCodeRedeemed?.();
    } catch {
      setCodeError(t('common.error_connection'));
    } finally {
      setCodeLoading(false);
    }
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', minHeight: '100vh', display: 'flex', flexDirection: 'column', background: C.cream, fontFamily: 'sans-serif' }}>

      <div style={{ background: C.sage, padding: '1rem 1.2rem', display: 'flex', alignItems: 'center', gap: '0.7rem', flexShrink: 0 }}>
        <img src="/logo192.png" style={{ width: '1.8rem', height: '1.8rem', objectFit: 'contain' }} alt="Vera" />
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '1.1rem' }}>Vera GLP</div>
          <div style={{ color: C.mintLt, fontSize: '0.75rem' }}>{t('landing.tagline')}</div>
        </div>
        <LanguageToggle light />
        <button
          onClick={() => window.location.href = '/?page=clinic-landing'}
          style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, color: '#fff', fontSize: '0.72rem', padding: '0.35rem 0.7rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          {t('landing.clinic_btn')}
        </button>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem 1rem' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '1.5rem', boxShadow: '0 2px 16px rgba(44,106,79,0.12)', border: `2px solid ${C.mint}`, width: '100%', maxWidth: 400 }}>
          <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: C.carbon, lineHeight: 1.5 }}>
            {t('landing.code_intro')}
          </p>
          <form onSubmit={handleRedeemCode}>
            <input
              type="text"
              value={codeInput}
              onChange={e => setCodeInput(e.target.value.toUpperCase())}
              placeholder={t('landing.code_placeholder')}
              required
              style={{ width: '100%', padding: '0.7rem 1rem', borderRadius: 8, border: `1.5px solid ${C.mint}`, background: C.cream, fontSize: '0.95rem', boxSizing: 'border-box', outline: 'none', marginBottom: '0.8rem', letterSpacing: '0.05em' }}
            />
            {codeError && (
              <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 8, padding: '0.6rem 0.9rem', fontSize: '0.85rem', marginBottom: '0.8rem' }}>
                {codeError}
              </div>
            )}
            <button type="submit" disabled={codeLoading} style={{
              width: '100%', padding: '0.85rem', borderRadius: 8, border: 'none',
              background: C.sage, color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
            }}>
              {codeLoading ? t('landing.loading_auth') : t('landing.code_submit_btn')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
